import { computed, ref, watch, onBeforeUnmount, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { isEmpty, debounce } from 'lodash-es';
import { useViewModeStore } from '@/common/stores/view-mode';
import { VIEW_MODE } from '@/common/define/common.define';
import router from '@/common/router';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { useSimpleTextTooltip } from '@/common/components/molecules/simpleTextTooltip/simpleTextTooltip.uses';
import { useInternational } from '@/common/locale';
import type { MenuInfo, PopoverMenuType } from './types';
import { useDashboardMenu, useMenu, hasNoChildren } from './baseMenu.uses';
import { useShowMainPopup } from './popups/popups.uses';
import { BASE_MENU_VALUE, MENU_HEIGHT } from './baseMenu.define';
import { useDashboardEditorViewStore } from '@/dashboard/stores/dashboard-editor-view';
import { DASHBOARD_MENU_TYPE } from '../../molecules/breadCrumbs/breadCrumbs.define';
import { useBaseMenuDrag } from './baseMenu.drag';

const SORTABLE_DRAG_DELAY = 300; // 드래그 활성화 지연 시간 (ms)

// CSS 변수를 JS 상수로 설정
const setMenuHeightCSSVariables = () => {
  const root = document.documentElement;
  root.style.setProperty('--gnb-sub-menu-element-height', `${MENU_HEIGHT.SUB_MENU_ELEMENT}px`);
  root.style.setProperty('--gnb-mid-menu-title-height', `${MENU_HEIGHT.MID_MENU_TITLE}px`);
  root.style.setProperty('--gnb-mid-menu-bar-height', `${MENU_HEIGHT.MID_MENU_BAR}px`);
  root.style.setProperty('--gnb-mid-menu-bar-height-large', `${MENU_HEIGHT.MID_MENU_BAR_LARGE}px`);
  root.style.setProperty('--gnb-menu-margin-bottom', `${MENU_HEIGHT.MARGIN_BOTTOM}px`);
};

export const setup = () => {
  // CSS 변수 초기 설정
  setMenuHeightCSSVariables();
  const { t } = useInternational();
  const {
    findMenuItem,
    menuInfo,
    isMenuReady,
    helpMenuInfo,
    hasHelpMenuPermission,
    myInfoMenuInfo,
  } = useMenu();
  const { viewMode, productName } = storeToRefs(useViewModeStore());
  const { dashboardMenuInfo, analysisBoardMenuInfo } = useDashboardMenu(menuInfo.value);

  const baseMenuStore = useBaseMenuStore();
  const {
    menuVisitRoute: menuVisitRouteStore,
    mainPanelInfo,
    subPanelInfo,
    isBaseMenuHidden,
    isShowMenuSearch,
  } = storeToRefs(baseMenuStore);
  const { isTabPanelOpen } = storeToRefs(useDashboardEditorViewStore());
  const {
    updateLastMenu,
    getLastMenu,
    getLastSubPanelMenu,
    openSubPanel,
    closeSubPanel,
    selectCategory,
    selectSubPanelMenu,
    toggleCategory,
    shrinkMainPanel,
    toggleBaseMenu,
    getMenuOrder,
    updateMenuOrder,
    cancelPendingMenuOrderSave,
  } = baseMenuStore;

  const { baseMenuRef, popupMenuRef, popupMenuStyle, setHoveredItemInfo } = useShowMainPopup();

  const isMyInfoMenuClicked = ref<boolean>(false);
  const hoveredMenuInfo = ref<MenuInfo | null>(null);
  const tooltipMouseEvent = ref<MouseEvent | null>(null);
  const menuListRef = ref<HTMLElement | null>(null);
  const orderedMenuInfo = ref<MenuInfo[]>([]);
  const isDragging = ref<boolean>(false);
  const onlyTitleFloatingPopup = computed<boolean>(() => {
    return hoveredMenuInfo.value?.children == null || hoveredMenuInfo.value?.children.length === 0;
  });

  const { onMouseEventInTarget } = useSimpleTextTooltip(tooltipMouseEvent);

  // 메뉴 순서 적용
  const applyMenuOrder = (menus: MenuInfo[]): MenuInfo[] => {
    const savedOrder = getMenuOrder();
    if (!savedOrder.length) {
      return menus;
    }

    const menuMap = new Map(menus.map((menu) => [menu.value, menu]));
    const ordered: MenuInfo[] = [];
    const unordered: MenuInfo[] = [];

    // 저장된 순서대로 정렬
    savedOrder.forEach((value) => {
      const menu = menuMap.get(value);
      if (menu) {
        ordered.push(menu);
        menuMap.delete(value);
      }
    });

    // 순서에 없는 메뉴는 뒤에 추가
    menuMap.forEach((menu) => {
      unordered.push(menu);
    });

    return [...ordered, ...unordered];
  };

  // BaseMenu drag/reorder — SortableList(useSortableDrag)와 동일한 패턴.
  // long-press 후 ghost를 document.body에 attach하여 마우스를 정확히 따라가게 함.
  useBaseMenuDrag({
    containerRef: menuListRef,
    draggableSelector: 'li.menu-element',
    longPressDelay: SORTABLE_DRAG_DELAY,
    onDragStart: () => {
      isDragging.value = true;
    },
    onDragEnd: () => {
      isDragging.value = false;
    },
    onReorder: (oldIndex, newIndex) => {
      const original = orderedMenuInfo.value;
      const draggable = original.filter(
        (m) => m.value !== BASE_MENU_VALUE.SETTINGS && m.value !== BASE_MENU_VALUE.HELP,
      );
      const [moved] = draggable.splice(oldIndex, 1);
      if (!moved) return;
      draggable.splice(newIndex, 0, moved);

      // updateMenuOrder는 debounce된 비동기 저장이라 store가 즉시 반영되지 않음.
      // settings/help 위치는 원래대로 두고 draggable 슬롯만 새 순서로 채워 즉시 재렌더링.
      let dragIdx = 0;
      orderedMenuInfo.value = original.map((m) => {
        if (m.value === BASE_MENU_VALUE.SETTINGS || m.value === BASE_MENU_VALUE.HELP) return m;
        const next = draggable[dragIdx];
        dragIdx += 1;
        return next ?? m;
      });
      updateMenuOrder(draggable.map((m) => m.value));
    },
  });

  const currentMenuVisitRoute = computed<MenuInfo[]>(() => {
    const visitRoute: MenuInfo[] = [];
    const { path, fullPath } = router.currentRoute.value;
    // 쿼리를 포함한 메뉴 path(예: Workloads의 /kubernetes/workload?type=deployments)도
    // 매칭되도록 fullPath를 우선 비교하고, 쿼리 없는 일반 메뉴는 기존처럼 path로 매칭한다.
    findMenuItem(
      menuInfo.value,
      (menu: MenuInfo) => menu.path === fullPath || menu.path === path,
      visitRoute,
    );
    return visitRoute;
  });

  const currentRootMenu = computed<string>(() => {
    const [, rootPath, ...subPaths] = router.currentRoute.value.path.split('/');
    if (!isEmpty(router.currentRoute.value.query) && router.currentRoute.value.query.rootMenu) {
      const { rootMenu } = router.currentRoute.value.query;
      return (rootMenu as string) ?? '';
    }

    if (
      rootPath === BASE_MENU_VALUE.DASHBOARD ||
      rootPath === BASE_MENU_VALUE.SETTINGS ||
      rootPath === BASE_MENU_VALUE.HOME
    ) {
      return rootPath;
    }
    if (rootPath === BASE_MENU_VALUE.FAULT_ANALYSIS) {
      return BASE_MENU_VALUE.APPLICATION;
    }
    if (
      ['singleView', 'multiView'].includes(subPaths[0]) &&
      viewMode.value !== VIEW_MODE.MAXGAUGE
    ) {
      return 'database';
    }

    if (!currentMenuVisitRoute.value.length) {
      return '';
    }
    return currentMenuVisitRoute.value[0].value ?? '';
  });

  const hasMenuContent = (menu: MenuInfo) => {
    return (
      menu.dashboardList ||
      menu.analysisBoardList ||
      (menu.children && menu.children.length) ||
      (menu.subMenuList && menu.subMenuList.length)
    );
  };

  const isAvailableMenu = (menu: MenuInfo) =>
    hasMenuContent(menu) || (menu.path && hasNoChildren(menu));

  const onClickMainCategory = (menu: MenuInfo) => {
    if (!isAvailableMenu(menu)) {
      return;
    }

    // path가 있고 children이 없는 메뉴는 바로 라우팅 (홈, 대시보드 등)
    if (
      menu.path &&
      hasNoChildren(menu) &&
      !menu.dashboardList &&
      !menu.analysisBoardList &&
      !menu.subMenuList
    ) {
      selectCategory({ menuValue: menu.value });
      mainPanelInfo.value.selectedSubMenu = undefined;
      closeSubPanel();
      selectSubPanelMenu('');
      router.push(menu.path);
      return;
    }

    // 이미 선택된 메뉴를 다시 클릭하면 메뉴바 축소/확장 토글
    if (menu.value === mainPanelInfo.value.selectedCategory) {
      toggleCategory(menu.value);

      // 메뉴가 확장될 때 저장된 3depth 상태 복원
      if (mainPanelInfo.value.isMainPanelExpanded) {
        const lastSelectedSubMenu = getLastSubPanelMenu(menu.value);
        if (lastSelectedSubMenu && subPanelInfo.value.openedSubPanelMenuInfo) {
          selectSubPanelMenu(lastSelectedSubMenu);
        }
      }
      return;
    }

    toggleCategory(menu.value);

    // 현재 라우트가 클릭한 메뉴와 관련이 없을 때
    if (currentRootMenu.value !== menu.value) {
      mainPanelInfo.value.selectedSubMenu = undefined;
      closeSubPanel(); // 우측 패널 닫기

      // 클릭한 메뉴에 이전에 선택된 3depth 상태가 있으면 복원
      const lastSelectedSubMenu = getLastSubPanelMenu(menu.value);
      if (lastSelectedSubMenu) {
        // 해당 3depth가 속한 2depth 메뉴 찾기
        const menuWithSubPanel = menu.children?.find((child) =>
          child.subMenuList?.some((sub) => sub.value === lastSelectedSubMenu),
        );
        if (menuWithSubPanel) {
          mainPanelInfo.value.selectedSubMenu = menuWithSubPanel.value;
          openSubPanel(menuWithSubPanel);
          selectSubPanelMenu(lastSelectedSubMenu);
        }
      }
    } else {
      // 현재 메뉴와 일치하면 현재 선택 상태 복원
      const { menuType, rootMenu } = router.currentRoute.value.query;
      // 경로에서 대시보드 ID 추출
      const pathMatch = router.currentRoute.value.path.match(/\/dashboard\/view\/([^/?]+)/);
      const id = pathMatch ? pathMatch[1] : null;

      // 대시보드/분석보드 메뉴인 경우
      if (menuType === DASHBOARD_MENU_TYPE.DASHBOARD && rootMenu === menu.value) {
        mainPanelInfo.value.selectedSubMenu = BASE_MENU_VALUE.DASHBOARD;
        openSubPanel({
          text: t('WORD.GNB.DASHBOARD'),
          value: 'dashboard',
          subMenuList: dashboardMenuInfo.value[menu.value] ?? [],
        });
        if (id) {
          selectSubPanelMenu(`dashboard_Dashboard View_${id}`);
        }
      } else if (menuType === DASHBOARD_MENU_TYPE.ANALYSIS_DASHBOARD && rootMenu === menu.value) {
        mainPanelInfo.value.selectedSubMenu = BASE_MENU_VALUE.ANALYSISBOARD;
        openSubPanel({
          text: t('WORD.GNB.ANALYSISBOARD'),
          value: 'analysisBoard',
          subMenuList: analysisBoardMenuInfo.value[menu.value] ?? [],
        });
        if (id) {
          selectSubPanelMenu(`dashboard_Dashboard View_${id}`);
        }
      } else {
        // 일반 메뉴인 경우 - 현재 페이지 기준으로 복원
        // onPathChange와 동일한 로직 사용
        const subMenuIdx = currentMenuVisitRoute.value.findIndex(
          (menuItem) => menuItem.subMenuList?.length,
        );

        if (currentMenuVisitRoute.value.length >= 2) {
          mainPanelInfo.value.selectedSubMenu = currentMenuVisitRoute.value[1].value;
        }

        if (subMenuIdx !== -1 && currentMenuVisitRoute.value[subMenuIdx]) {
          openSubPanel(currentMenuVisitRoute.value[subMenuIdx]);

          // 현재 경로와 일치하는 3depth 메뉴가 있으면 우선 사용
          if (currentMenuVisitRoute.value.length > subMenuIdx + 1) {
            const targetDepthMenu =
              currentMenuVisitRoute.value[currentMenuVisitRoute.value.length - 1];
            selectSubPanelMenu(targetDepthMenu.value);
          } else {
            // 현재 경로와 일치하지 않으면 해당 메뉴의 마지막 선택 상태 복원
            const lastSelectedSubMenu = getLastSubPanelMenu(menu.value);
            if (lastSelectedSubMenu) {
              selectSubPanelMenu(lastSelectedSubMenu);
            }
          }
        } else {
          const lastSelectedSubMenu = getLastSubPanelMenu(menu.value);
          if (lastSelectedSubMenu) {
            const menuWithSubPanel = menu.children?.find((child) =>
              child.subMenuList?.some((sub) => sub.value === lastSelectedSubMenu),
            );
            if (menuWithSubPanel) {
              openSubPanel(menuWithSubPanel);
              selectSubPanelMenu(lastSelectedSubMenu);
            }
          }
        }
      }
    }

    if (hoveredMenuInfo.value) {
      hoveredMenuInfo.value = null;
    }
  };

  const onClickMyInfoCategory = (evt: MouseEvent) => {
    setHoveredItemInfo(evt);
    toggleCategory(BASE_MENU_VALUE.SETTINGS);
    isMyInfoMenuClicked.value = true;
  };

  const onClickSupportCategory = () => {
    toggleCategory(BASE_MENU_VALUE.HELP);
    openSubPanel(helpMenuInfo);
    const selectedChildren = helpMenuInfo.subMenuList[0].children?.find(({ path }) =>
      currentRootMenu.value === BASE_MENU_VALUE.PA
        ? path?.endsWith(BASE_MENU_VALUE.ANALYSIS)
        : path?.endsWith(currentRootMenu.value),
    );
    if (selectedChildren && selectedChildren.path) {
      selectSubPanelMenu(selectedChildren.value);
      router.push(selectedChildren.path);
    }
  };

  const restoreMenuState = () => {
    const [depth1, depth2, depth3] = currentMenuVisitRoute.value;
    const menuValue = depth1?.value || currentRootMenu.value;

    // 1depth / 2depth 선택 상태 복원
    selectCategory({
      menuValue,
      subMenuValue: depth2?.value,
    });

    // 3depth 선택 상태 복원
    if (depth3?.value) {
      selectSubPanelMenu(depth3.value);
    }
  };

  const onShrinkMainPanel = () => {
    if (
      subPanelInfo.value.openedSubPanelMenuInfo &&
      subPanelInfo.value.openedSubPanelMenuInfo.value !== BASE_MENU_VALUE.SETTINGS
    ) {
      closeSubPanel();
    }
    if (mainPanelInfo.value.isMainPanelExpanded) {
      shrinkMainPanel();
    }

    // 닫힐때 route 이동 없으면 현재 route 메뉴로 복원
    restoreMenuState();
  };

  const onHoverMainCategory = (evt: MouseEvent, menu: MenuInfo) => {
    if (
      isDragging.value ||
      mainPanelInfo.value.isMainPanelExpanded ||
      subPanelInfo.value.openedSubPanelMenuInfo ||
      isMyInfoMenuClicked.value ||
      evt.button !== 0
    ) {
      return; // nav expanded 되었을 경우에는 hover 동작 X
    }

    if (!hasMenuContent(menu)) {
      // 하위 메뉴가 off, 최상위 메뉴만 on일 경우
      // 26.01.20 - 디자인에서 타이틀만 그냥 보여주는 형태 요청
      // hoveredMenuInfo.value = null;
      // return;
    }

    const menuValue = setHoveredItemInfo(evt);

    hoveredMenuInfo.value = findMenuItem(menuInfo.value, (info) => info.value === menuValue);
  };

  const onHoverHelpCategory = (evt: MouseEvent) => {
    if (isMyInfoMenuClicked.value || evt.button !== 0) {
      return;
    }

    const menuValue = setHoveredItemInfo(evt);
    if (menuValue === BASE_MENU_VALUE.HOME) {
      hoveredMenuInfo.value = null;
    } else if (menuValue === BASE_MENU_VALUE.SETTINGS) {
      hoveredMenuInfo.value = myInfoMenuInfo;
    } else if (menuValue === BASE_MENU_VALUE.HELP) {
      hoveredMenuInfo.value = helpMenuInfo;
    }
  };

  const debouncedLeavePopoverHandler = debounce((menuType: PopoverMenuType) => {
    const popupElement = document.querySelector(menuType.selector) as HTMLElement;

    if (hoveredMenuInfo.value?.value === menuType.value) {
      if (!popupElement?.matches(':hover')) {
        hoveredMenuInfo.value = null;
      }
    }
  }, 100);

  const onLeavePopoverMenu = (menuType: PopoverMenuType) => {
    debouncedLeavePopoverHandler(menuType);
  };

  const onHideFloatingMenu = () => {
    hoveredMenuInfo.value = null;
  };

  const onHideMyInfoPopup = () => {
    isMyInfoMenuClicked.value = false;
  };

  const setSettingPanel = () => {
    openSubPanel(myInfoMenuInfo);
    selectSubPanelMenu(currentMenuVisitRoute.value[2].value);
  };

  const setHelpPanel = () => {
    openSubPanel(helpMenuInfo);
    selectSubPanelMenu(currentMenuVisitRoute.value[2].value);
  };

  const onClickSetting = () => {
    let targetPath = getLastMenu('myInfo');

    if (!targetPath || !targetPath.length) {
      targetPath = '/myInfo';
    }

    onHideMyInfoPopup();
    if (targetPath === router.currentRoute.value.path) {
      setSettingPanel();
    }
    router.push(targetPath);
  };

  const onClickTitle = () => {
    // 접혀있을 때는 확장, 확장되어 있을 때는 홈으로 이동
    if (!mainPanelInfo.value.isMainPanelExpanded) {
      toggleCategory();
    } else {
      router.push('/');
    }
  };

  const onClickOutsideMainPanel = () => {
    // 설정 화면에서만 적용됨 DSP-15084
    if (currentRootMenu.value === BASE_MENU_VALUE.SETTINGS) {
      // 현재 페이지는 설정이지만, 우측 패널의 컨텐츠가 설정이 아닌 경우에는 GNB 닫힐 때 같이 닫아준다.
      if (
        subPanelInfo.value.openedSubPanelMenuInfo &&
        subPanelInfo.value.openedSubPanelMenuInfo.value !== BASE_MENU_VALUE.SETTINGS
      ) {
        closeSubPanel();
      }

      shrinkMainPanel();
      // 닫힐때 route 이동 없으면 현재 route 메뉴로 복원
      restoreMenuState();
    }
  };

  const getMenuInfoByRoutePath = () => {
    // 경로 변경될 때마다 우측 패널 내용물 검사
    // 경로변경 예시: 뒤로가기, 링크클릭 등
    const subMenuIdx = currentMenuVisitRoute.value.findIndex((menu) => menu.subMenuList?.length);
    const { rootMenu, menuType } = router.currentRoute.value.query; // for dashboard & analysisboard
    // 경로에서 대시보드 ID 추출
    const pathMatch = router.currentRoute.value.path.match(/\/dashboard\/view\/([^/?]+)/);
    const id = pathMatch ? pathMatch[1] : null; // for dashboard & analysisboard

    return {
      subMenuIdx,
      rootMenu,
      menuType,
      id,
    };
  };

  const onPathChange = () => {
    const { subMenuIdx, rootMenu, menuType, id } = getMenuInfoByRoutePath();

    if (currentRootMenu.value === BASE_MENU_VALUE.SETTINGS) {
      setSettingPanel();
      return;
    }
    if (currentRootMenu.value === BASE_MENU_VALUE.HELP) {
      setHelpPanel();
      return;
    }

    if (subPanelInfo.value.openedSubPanelMenuInfo) {
      const keepOpenValue: string[] = [
        BASE_MENU_VALUE.KUBERNETES,
        BASE_MENU_VALUE.CLOUD,
        BASE_MENU_VALUE.APPLICATION,
        BASE_MENU_VALUE.DATABASE,
      ];
      const lastMenu =
        currentMenuVisitRoute.value.length > 0
          ? currentMenuVisitRoute.value[currentMenuVisitRoute.value.length - 1]
          : undefined;

      if (keepOpenValue.includes(currentRootMenu.value) && subMenuIdx !== -1) {
        openSubPanel(currentMenuVisitRoute.value[subMenuIdx]);
        if (lastMenu) {
          selectSubPanelMenu(lastMenu.value);
        }
      } else if (menuType === DASHBOARD_MENU_TYPE.DASHBOARD) {
        openSubPanel({
          text: t('WORD.GNB.DASHBOARD'),
          value: 'dashboard',
          subMenuList: dashboardMenuInfo.value[rootMenu as string] ?? [],
        });
        selectSubPanelMenu(`dashboard_Dashboard View_${id}`);
      } else if (menuType === DASHBOARD_MENU_TYPE.ANALYSIS_DASHBOARD) {
        openSubPanel({
          text: t('WORD.GNB.ANALYSISBOARD'),
          value: 'analysisBoard',
          subMenuList: analysisBoardMenuInfo.value[rootMenu as string] ?? [],
        });
        selectSubPanelMenu(`dashboard_Dashboard View_${id}`);
      } else {
        closeSubPanel();
      }
    } else if (
      menuType === DASHBOARD_MENU_TYPE.DASHBOARD ||
      menuType === DASHBOARD_MENU_TYPE.ANALYSIS_DASHBOARD
    ) {
      selectCategory({ menuValue: currentRootMenu.value, subMenuValue: menuType });
    }
  };

  watch(
    router.currentRoute,
    (newRoute) => {
      toggleBaseMenu(false);
      updateLastMenu(currentRootMenu.value, newRoute.fullPath);
      menuVisitRouteStore.value = currentMenuVisitRoute.value;

      const subMenuIdx = currentMenuVisitRoute.value.findIndex((menu) => menu.subMenuList?.length);
      let subMenuValue: string | undefined;

      if (subMenuIdx !== -1) {
        // subMenuList를 가진 메뉴가 있으면 그 메뉴의 value를 subMenuValue로 설정
        subMenuValue = currentMenuVisitRoute.value[subMenuIdx].value;
      } else {
        const SUB_MENU_IDX = currentMenuVisitRoute.value.length >= 3 ? 2 : 1;
        subMenuValue = currentMenuVisitRoute.value?.[SUB_MENU_IDX]?.value;
      }

      selectCategory({
        menuValue: currentRootMenu.value,
        subMenuValue,
      });

      // NOTE: 이 밑의 로직은 GNB 설정값 계정 저장시 전체적으로 바꿀 필요 있음
      // 기본적으로 settings, help 의 경우 관련 페이지 접근 시 우측 패널 열리게끔 짜여져 있으나
      // GNB 설정값 계정 저장 기능 구현할 경우 우측 패널 자동 Open 기능이 무의미하기 때문

      onPathChange();
    },
    { immediate: true },
  );

  watch(
    [dashboardMenuInfo, analysisBoardMenuInfo],
    ([dashboardMenu, analysisMenu]) => {
      // NOTE: 대시보드 메뉴랑 분석보드 메뉴는 서버에서 받아오기 때문에 업데이트가 느림
      // 경로 바꾸는 시점에서 해당 메뉴목록이 비어있을 수 있어서 watch 통해 refetch 필요
      if (!subPanelInfo.value.openedSubPanelMenuInfo) {
        return;
      }

      const openedMenu = subPanelInfo.value.openedSubPanelMenuInfo;
      const isDashboard = openedMenu.value === BASE_MENU_VALUE.DASHBOARD;
      const isAnalysisBoard = openedMenu.value === BASE_MENU_VALUE.ANALYSISBOARD;

      if (!isDashboard && !isAnalysisBoard) {
        return;
      }

      // subMenuList에서 rootMenu와 menuType 추출 (현재 라우트가 아닌 열려있는 패널의 정보 사용)
      let rootMenu: string | undefined;
      let menuType: string | undefined;

      // subMenuList의 항목들에서 rootMenu와 menuType 추출
      if (openedMenu.subMenuList && openedMenu.subMenuList.length > 0) {
        const firstMenuItem = openedMenu.subMenuList[0];
        if (firstMenuItem?.path) {
          const rootMenuMatch = firstMenuItem.path.match(/[?&]rootMenu=([^&]+)/);
          const menuTypeMatch = firstMenuItem.path.match(/[?&]menuType=([^&]+)/);
          if (rootMenuMatch) rootMenu = rootMenuMatch[1];
          if (menuTypeMatch) menuType = menuTypeMatch[1];
        }
      }

      // subMenuList가 비어있거나 추출 실패한 경우에만 현재 라우트의 query 사용
      if (!rootMenu || !menuType) {
        const query = router.currentRoute.value.query;
        rootMenu = query.rootMenu as string;
        menuType = query.menuType as string;
      }

      if (!rootMenu || !menuType) {
        return;
      }

      const menuList =
        menuType === DASHBOARD_MENU_TYPE.DASHBOARD
          ? (dashboardMenu[rootMenu] ?? [])
          : (analysisMenu[rootMenu] ?? []);

      subPanelInfo.value.openedSubPanelMenuInfo = {
        ...openedMenu,
        subMenuList: [...menuList],
      };
    },
    { deep: true },
  );

  // 메뉴 정보 변경 시 순서 적용
  // 드래그 중에는 onReorder의 낙관적 업데이트가 우선이므로 menuInfo의 reactivity 변경을
  // 무시한다. 드래그 종료 후 menuInfo가 다시 변경되면 watch가 재진입해 동기화된다.
  watch(
    menuInfo,
    (newMenuInfo) => {
      if (isDragging.value) return;
      orderedMenuInfo.value = applyMenuOrder(newMenuInfo);
    },
    { immediate: true, deep: true },
  );

  const handleEscKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || isShowMenuSearch.value || isTabPanelOpen.value) return;
    if (mainPanelInfo.value.isMainPanelExpanded) onShrinkMainPanel();
  };

  onMounted(() => {
    document.addEventListener('keydown', handleEscKey);
  });

  onBeforeUnmount(() => {
    debouncedLeavePopoverHandler.cancel();
    cancelPendingMenuOrderSave();
    document.removeEventListener('keydown', handleEscKey);
  });

  const shouldShowFloatingMenu = computed<boolean>(() => {
    return (
      !!hoveredMenuInfo.value?.value &&
      hoveredMenuInfo.value.value !== BASE_MENU_VALUE.HELP &&
      hoveredMenuInfo.value.value !== BASE_MENU_VALUE.SETTINGS &&
      !mainPanelInfo.value.isMainPanelExpanded
    );
  });

  return {
    t,

    menuInfo: orderedMenuInfo,
    isMenuReady,
    dashboardMenuInfo,
    analysisBoardMenuInfo,
    helpMenuInfo,
    myInfoMenuInfo,

    productName,
    hasHelpMenuPermission,

    mainPanelInfo,
    hoveredMenuInfo,
    isMyInfoMenuClicked,
    isDragging,

    baseMenuRef,
    popupMenuRef,
    popupMenuStyle,
    tooltipMouseEvent,
    isBaseMenuHidden,
    menuListRef,

    shouldShowFloatingMenu,
    onlyTitleFloatingPopup,

    onMouseEventInTarget,
    onHoverMainCategory,
    onHoverHelpCategory,
    onLeavePopoverMenu,
    onClickMainCategory,
    onClickMyInfoCategory,
    onClickSupportCategory,
    onShrinkMainPanel,
    onHideFloatingMenu,
    onHideMyInfoPopup,
    onClickTitle,
    onClickSetting,
    onClickOutsideMainPanel,
  };
};
